// src/components/CanvasArea.jsx
import React, { useEffect, useRef, useState } from "react";
import { Box, IconButton, Typography, Fade } from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import FitScreenIcon from "@mui/icons-material/FitScreen";

import useCanvas from "../hooks/useCanvas";
import LineList from "./LineList";
import ResponsiveOrderContainer from "./ResponsiveOrderContainer";
import { useData } from "../hooks/useData";
import { useDimensions } from "../hooks/useDimensions";
import { usePricing } from "../hooks/usePricing";
import useCanvasEvents from "../hooks/useCanvasEvents";

export default function CanvasArea() {
  const {
    canvas: savedCanvas,
    setCanvas,
    lines,
    addLine,
    selectedProduct,
  } = useData();
  const { updateDimensions } = useDimensions();
  const { updatePricing } = usePricing();
  const containerRef = useRef(null);

  // INITIAL CANVAS
  const fabricCanvas = useCanvas("canvas", { w: 100, h: 100 });

  // RESIZE CANVAS TO FIT CONTAINER
  useEffect(() => {
    if (!fabricCanvas || !containerRef.current) return;

    const resize = () => {
      const rect = containerRef.current.getBoundingClientRect();
      const width = rect.width;
      const height = rect.height;

      const canvasEl = fabricCanvas.lowerCanvasEl;
      if (canvasEl) {
        canvasEl.width = width;
        canvasEl.height = height;
        canvasEl.style.width = width + "px";
        canvasEl.style.height = height + "px";
      }

      fabricCanvas.setDimensions({ width, height });
      fabricCanvas.renderAll();
    };

    resize();
    window.addEventListener("resize", resize);
    const observer = new ResizeObserver(resize);
    observer.observe(containerRef.current);

    return () => {
      window.removeEventListener("resize", resize);
      observer.disconnect();
    };
  }, [fabricCanvas]);

  useEffect(() => {
    if (fabricCanvas && !savedCanvas) setCanvas(fabricCanvas);
  }, [fabricCanvas, savedCanvas, setCanvas]);

  useEffect(() => {
    if (lines.length === 0 && selectedProduct) addLine();
  }, [lines.length, selectedProduct, addLine]);

  useCanvasEvents({
    canvas: fabricCanvas,
    onUpdateDimensions: updateDimensions,
    onUpdatePricing: updatePricing,
  });

  // --- ROBUST AUTO-FIT & ZOOM LOGIC ---
  const [showZoomUI, setShowZoomUI] = useState(false);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    if (!fabricCanvas) return;

    const runAutoFit = () => {
      // 1. Get all text objects that aren't empty
      const objects = fabricCanvas
        .getObjects()
        .filter((o) => o.type === "i-text" && o.text?.trim());

      const canvasWidth = fabricCanvas.getWidth();
      // Safe padding: 40px on mobile, 80px on desktop
      const padding = canvasWidth < 600 ? 30 : 60;
      const availableWidth = canvasWidth - padding;

      if (objects.length === 0 || availableWidth <= 0) {
        fabricCanvas.setZoom(1);
        setZoom(1);
        setShowZoomUI(false);
        return;
      }

      // 2. Find the widest line of text
      let maxObjWidth = 0;
      objects.forEach((obj) => {
        maxObjWidth = Math.max(maxObjWidth, obj.getScaledWidth());
      });

      // 3. Calculate Zoom
      if (maxObjWidth > availableWidth) {
        const autoZoom = availableWidth / maxObjWidth;
        const finalZoom = Math.max(autoZoom, 0.15); // Don't go below 15%
        fabricCanvas.setZoom(finalZoom);
        setShowZoomUI(true);
      } else {
        fabricCanvas.setZoom(1);
        setShowZoomUI(false);
      }

      setZoom(fabricCanvas.getZoom());
      fabricCanvas.requestRenderAll();
    };

    // Trigger on render, resize, and text changes
    fabricCanvas.on("after:render", runAutoFit);
    window.addEventListener("resize", runAutoFit);

    // Initial "Force" run to handle slow font loading
    const timer = setTimeout(runAutoFit, 500);

    return () => {
      fabricCanvas.off("after:render", runAutoFit);
      window.removeEventListener("resize", runAutoFit);
      clearTimeout(timer);
    };
  }, [fabricCanvas]);

  const zoomIn = () => fabricCanvas.setZoom(fabricCanvas.getZoom() * 1.1);
  const zoomOut = () => fabricCanvas.setZoom(fabricCanvas.getZoom() * 0.9);
  const zoomFit = () => fabricCanvas.setZoom(1);

  return (
    <>
      {/* CANVAS CONTAINER */}
      <Box
        ref={containerRef}
        id="canvas-container"
        sx={{
          width: "100%",
          // Responsive height: ensures usable workspace on all devices
          height: { xs: "35vh", sm: "45vh", md: 460 },
          bgcolor: "#ffffff",
          backgroundImage: `
            linear-gradient(45deg, #f9f9f9 25%, transparent 25%),
            linear-gradient(-45deg, #f9f9f9 25%, transparent 25%),
            linear-gradient(45deg, transparent 75%, #f9f9f9 75%),
            linear-gradient(-45deg, transparent 75%, #f9f9f9 75%)`,
          backgroundSize: "20px 20px",
          backgroundPosition: "0 0, 0 10px, 10px -10px, -10px 0px",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          overflow: "hidden",
          position: "relative",
          mb: 2,
          boxShadow: "inset 0 2px 4px rgba(0,0,0,0.05)",
        }}
      >
        <canvas id="canvas" />

        {/* FLOATING ZOOM CONTROLS */}
        <Fade in={showZoomUI}>
          <Box
            sx={{
              position: "absolute",
              bottom: 16,
              right: 16,
              zIndex: 10,
              bgcolor: "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(4px)",
              border: "1px solid",
              borderColor: "primary.main",
              borderRadius: 3,
              display: "flex",
              alignItems: "center",
              p: 0.5,
              boxShadow: 3,
            }}
          >
            <IconButton size="small" onClick={zoomOut} color="primary">
              <ZoomOutIcon fontSize="small" />
            </IconButton>
            <Typography
              variant="caption"
              sx={{
                mx: 1,
                fontWeight: "bold",
                minWidth: "35px",
                textAlign: "center",
              }}
            >
              {Math.round(zoom * 100)}%
            </Typography>
            <IconButton size="small" onClick={zoomFit} color="primary">
              <FitScreenIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={zoomIn} color="primary">
              <ZoomInIcon fontSize="small" />
            </IconButton>
          </Box>
        </Fade>
      </Box>

      {/* LINE LIST AREA */}
      <Box
        sx={{
          bgcolor: "background.paper",
          p: { xs: 1, sm: 2 },
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          mt: -1,
          position: "relative",
          zIndex: 2,
        }}
      >
        <LineList />
      </Box>

      {/* MOBILE BOTTOM SHEET / DESKTOP DRAWER */}
      <ResponsiveOrderContainer />
    </>
  );
}
